import type { TeamContractRules } from '../services/teams/teamService';

export function canPersistContractDraft(rules: TeamContractRules): boolean {
  return rules.allowContractDraft;
}

export type SerializedDraftPersister<T> = {
  enqueue(save: () => Promise<T>): Promise<T>;
  /** Aguarda a persistência em voo (se houver). */
  flush(): Promise<void>;
  /** Descarta o próximo save pendente (não cancela o que já está em voo). */
  discardPending(): void;
};

/**
 * Mantém no máximo uma persistência de rascunho em voo. Alterações feitas
 * durante uma requisição substituem o próximo save pendente pelo estado mais
 * recente, sem descartar essa alteração.
 */
export function createSerializedDraftPersister<T>(): SerializedDraftPersister<T> {
  let pendingSave: (() => Promise<T>) | null = null;
  let drainPromise: Promise<T> | null = null;

  const drain = async (): Promise<T> => {
    let latestResult: T | undefined;

    try {
      while (pendingSave) {
        const save = pendingSave;
        pendingSave = null;
        latestResult = await save();
      }

      return latestResult as T;
    } finally {
      drainPromise = null;
    }
  };

  return {
    enqueue(save: () => Promise<T>): Promise<T> {
      pendingSave = save;
      if (!drainPromise) {
        drainPromise = drain();
      }
      return drainPromise;
    },
    flush(): Promise<void> {
      return drainPromise ? drainPromise.then(() => undefined) : Promise.resolve();
    },
    discardPending(): void {
      pendingSave = null;
    },
  };
}
